import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Save, X, ArrowLeft, Upload, ChevronUp, ChevronDown, GripVertical, Plus, Trash2, Eye, Loader2, RotateCw, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// AutoComplete component for categories and scope items
const AutoCompleteInput = ({ value, onChange, suggestions, placeholder, label }) => {
  const [inputValue, setInputValue] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);

  const items = value ? value.split(',').map(v => v.trim()).filter(v => v) : [];

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    
    if (val.trim()) {
      const filtered = suggestions.filter(
        s => s.toLowerCase().includes(val.toLowerCase()) && !items.includes(s)
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const addItem = (item) => {
    const newItems = [...items, item];
    onChange(newItems.join(', '));
    setInputValue('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const removeItem = (itemToRemove) => {
    const newItems = items.filter(i => i !== itemToRemove);
    onChange(newItems.join(', '));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addItem(inputValue.trim());
    } else if (e.key === 'Backspace' && !inputValue && items.length > 0) {
      removeItem(items[items.length - 1]);
    }
  };

  return (
    <div className="relative">
      <Label>{label}</Label>
      <div className="mt-1 border rounded-md p-2 bg-white focus-within:ring-2 focus-within:ring-cyan-500 focus-within:border-cyan-500">
        <div className="flex flex-wrap gap-2 mb-2">
          {items.map((item, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-100 text-cyan-700 rounded text-sm"
            >
              {item}
              <button
                type="button"
                onClick={() => removeItem(item)}
                className="hover:text-cyan-900"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={items.length === 0 ? placeholder : 'Add more...'}
          className="w-full outline-none text-sm"
        />
      </div>
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
          {filteredSuggestions.map((suggestion, idx) => (
            <button
              key={idx}
              type="button"
              className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 focus:bg-slate-100"
              onClick={() => addItem(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Image Upload Component with progress and optimization info
const ImageUploader = ({ onUpload, multiple = false, label }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ total: 0, completed: 0 });
  const [lastUploadInfo, setLastUploadInfo] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setProgress({ total: files.length, completed: 0 });
    setLastUploadInfo(null);
    
    const urls = [];
    let totalOriginalSize = 0;
    let totalOptimizedSize = 0;
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        
        try {
          const response = await axios.post(`${BACKEND_URL}/api/projects/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          urls.push(response.data.url);
          totalOriginalSize += response.data.original_size || 0;
          totalOptimizedSize += response.data.optimized_size || 0;
        } catch (err) {
          console.error(`Failed to upload ${file.name}:`, err);
        }
        
        setProgress(prev => ({ ...prev, completed: i + 1 }));
      }

      if (urls.length > 0) {
        onUpload(multiple ? urls : urls[0]);
        
        // Show optimization info
        if (totalOriginalSize > 0) {
          const savedBytes = totalOriginalSize - totalOptimizedSize;
          const savedPercent = Math.round((savedBytes / totalOriginalSize) * 100);
          setLastUploadInfo({
            count: urls.length,
            originalSize: (totalOriginalSize / 1024 / 1024).toFixed(2),
            optimizedSize: (totalOptimizedSize / 1024 / 1024).toFixed(2),
            savedPercent
          });
        }
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload image(s). Please try again.');
    } finally {
      setUploading(false);
      setProgress({ total: 0, completed: 0 });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div>
      {label && <Label>{label}</Label>}
      <div className="mt-1">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full border-dashed"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Uploading {progress.completed}/{progress.total}...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              {multiple ? 'Upload Images' : 'Upload Image'}
            </>
          )}
        </Button>
        
        {/* Upload optimization info */}
        {lastUploadInfo && (
          <div className="mt-2 text-xs text-green-600 bg-green-50 rounded-lg p-2">
            ✓ Uploaded {lastUploadInfo.count} image(s) • 
            {lastUploadInfo.originalSize}MB → {lastUploadInfo.optimizedSize}MB 
            ({lastUploadInfo.savedPercent}% smaller)
          </div>
        )}
      </div>
    </div>
  );
};

// Categorized Image Gallery Component
const CategorizedImageGallery = ({ category, images, onImagesChange, categoryLabel, categoryColor }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [rotatingIndex, setRotatingIndex] = useState(null);

  const handleUpload = (urls) => {
    onImagesChange([...images, ...urls]);
  };

  const handleRemove = (index) => {
    onImagesChange(images.filter((_, i) => i !== index));
  };

  const handleRotate = async (index, direction) => {
    const imageUrl = images[index];
    
    // Check if rotation is supported
    const cleanUrl = imageUrl.split('?')[0];
    const isCloudinary = cleanUrl.includes('cloudinary.com') || cleanUrl.includes('res.cloudinary.com');
    const isLocalUpload = cleanUrl.startsWith('/api/uploads/');
    
    if (!isCloudinary && !isLocalUpload) {
      alert('Cannot rotate this image. Only Cloudinary or locally uploaded images can be rotated.');
      return;
    }
    
    setRotatingIndex(index);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/projects/rotate-image`, {
        image_url: imageUrl,
        direction: direction
      });
      
      if (response.data.success) {
        const newImages = [...images];
        
        // Use the new URL from response if available, otherwise add cache buster
        if (response.data.url) {
          newImages[index] = response.data.url;
        } else {
          const cacheBuster = `?t=${Date.now()}`;
          newImages[index] = cleanUrl + cacheBuster;
        }
        
        onImagesChange(newImages);
        
        // Clear loaded state to show loading animation
        setLoadedImages(prev => ({ ...prev, [index]: false }));
        setImageErrors(prev => ({ ...prev, [index]: false }));
      }
    } catch (error) {
      console.error('Failed to rotate image:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to rotate image. Please try again.';
      alert(errorMsg);
    } finally {
      setRotatingIndex(null);
    }
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newImages = [...images];
    [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
    onImagesChange(newImages);
  };

  const handleMoveDown = (index) => {
    if (index === images.length - 1) return;
    const newImages = [...images];
    [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
    onImagesChange(newImages);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);
    onImagesChange(newImages);

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Get thumbnail URL from full image URL
  const getThumbnailUrl = (imageUrl) => {
    if (!imageUrl) return '';
    
    // Handle Cloudinary URLs - add transformation for thumbnail
    if (imageUrl.includes('cloudinary.com') || imageUrl.includes('res.cloudinary.com')) {
      // Insert transformation parameters into Cloudinary URL
      // Format: .../upload/[transformations]/[path]
      const uploadIndex = imageUrl.indexOf('/upload/');
      if (uploadIndex !== -1) {
        const prefix = imageUrl.substring(0, uploadIndex + 8); // includes '/upload/'
        const suffix = imageUrl.substring(uploadIndex + 8);
        // Remove any existing transformations (starts with 'v' for version or transformation params)
        const cleanSuffix = suffix.replace(/^v\d+\//, ''); // Remove version
        return `${prefix}w_300,h_300,c_fill,q_auto/${cleanSuffix}`;
      }
      return imageUrl;
    }
    
    // Handle local uploads - try to get the thumbnail
    if (imageUrl.startsWith('/api/uploads/') && !imageUrl.includes('/thumbnails/')) {
      const filename = imageUrl.split('/').pop().split('?')[0]; // Remove query params
      const thumbFilename = filename.replace(/\.([^.]+)$/, '_thumb.jpg');
      return `/api/uploads/thumbnails/${thumbFilename}`;
    }
    
    return imageUrl;
  };

  // Check if image can be rotated (Cloudinary or local upload)
  const canRotate = (imageUrl) => {
    if (!imageUrl) return false;
    const cleanUrl = imageUrl.split('?')[0];
    return cleanUrl.includes('cloudinary.com') || 
           cleanUrl.includes('res.cloudinary.com') || 
           cleanUrl.startsWith('/api/uploads/');
  };

  // Extract filename from URL
  const getFilename = (imageUrl) => {
    if (!imageUrl) return '';
    const parts = imageUrl.split('/');
    return parts[parts.length - 1].split('?')[0]; // Remove query params
  };

  const handleImageLoad = (idx) => {
    setLoadedImages(prev => ({ ...prev, [idx]: true }));
  };

  const handleImageError = (idx) => {
    setImageErrors(prev => ({ ...prev, [idx]: true }));
  };

  return (
    <div>
      {categoryLabel && (
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-slate-800">{categoryLabel}</h4>
          <span className="text-sm text-slate-500">{images.length} image(s)</span>
        </div>
      )}
      
      <ImageUploader
        onUpload={handleUpload}
        multiple={true}
      />
      
      {images.length > 0 && (
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
          {images.map((image, idx) => {
            const thumbnailUrl = getThumbnailUrl(image);
            const filename = getFilename(image);
            const hasError = imageErrors[idx];
            const isLoaded = loadedImages[idx];
            
            return (
              <div 
                key={idx}
                draggable
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDragLeave={() => setDragOverIndex(null)}
                onDrop={(e) => handleDrop(e, idx)}
                onDragEnd={() => { setDraggedIndex(null); setDragOverIndex(null); }}
                className={`relative group rounded-lg overflow-hidden cursor-grab active:cursor-grabbing transition-all shadow-sm hover:shadow-md ${
                  draggedIndex === idx ? 'opacity-50 scale-95' : ''
                } ${
                  dragOverIndex === idx ? 'ring-2 ring-cyan-500 ring-offset-2' : ''
                }`}
              >
                {/* Loading placeholder or rotating spinner */}
                {(!isLoaded && !hasError) || rotatingIndex === idx ? (
                  <div className="absolute inset-0 bg-slate-200 animate-pulse flex items-center justify-center z-10">
                    <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                  </div>
                ) : null}
                
                {/* Image */}
                <div className="aspect-square">
                  <img
                    src={hasError ? (image.startsWith('/api') ? `${BACKEND_URL}${image}` : image) : (thumbnailUrl.startsWith('/api') ? `${BACKEND_URL}${thumbnailUrl}` : thumbnailUrl)}
                    alt={`${category} ${idx + 1}`}
                    className={`w-full h-full object-cover transition-opacity ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                    loading="lazy"
                    onLoad={() => handleImageLoad(idx)}
                    onError={() => handleImageError(idx)}
                  />
                </div>
                
                {/* Hover overlay with actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1 p-1">
                  {/* Row 1: Delete and View */}
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => handleRemove(idx)}
                      className="w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-lg"
                      title="Remove image"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <a
                      href={image.startsWith('/api') ? `${BACKEND_URL}${image}` : image}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-7 h-7 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 shadow-lg"
                      title="View full size"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Eye className="w-4 h-4" />
                    </a>
                  </div>
                  
                  {/* Row 2: Rotate buttons (for Cloudinary and local uploads) */}
                  {canRotate(image) && (
                    <div className="flex gap-1 mt-1">
                      <button
                        type="button"
                        onClick={() => handleRotate(idx, 'counterclockwise')}
                        disabled={rotatingIndex !== null}
                        className="w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center hover:bg-amber-600 shadow-lg disabled:opacity-50"
                        title="Rotate counter-clockwise"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRotate(idx, 'clockwise')}
                        disabled={rotatingIndex !== null}
                        className="w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center hover:bg-amber-600 shadow-lg disabled:opacity-50"
                        title="Rotate clockwise"
                      >
                        <RotateCw className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Index badge */}
                <div className="absolute top-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded font-medium">
                  {idx + 1}
                </div>
                
                {/* Filename tooltip */}
                <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-white text-xs px-1 py-0.5 truncate opacity-0 group-hover:opacity-100 transition-opacity" title={filename}>
                  {filename.length > 20 ? `${filename.slice(0, 8)}...${filename.slice(-8)}` : filename}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AdminEditProjectPage = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const isNewProject = projectId === 'new';
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [loading, setLoading] = useState(!isNewProject);
  const [saving, setSaving] = useState(false);
  
  // Metadata for autocomplete
  const [allCategories, setAllCategories] = useState([]);
  const [allScopeItems, setAllScopeItems] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    featured_image: '',
    categories: '',
    client_name: '',
    location: '',
    status: 'Completed',
    scope_of_work: '',
    highlighted_scope: [],
    testimonial_quote: '',
    testimonial_author: '',
    video_url: '',
    video_title: '',
    testimonial_video_url: '',
    testimonial_video_title: '',
    images: [],
    categorized_images: {
      before: [],
      progress: [],
      completion: []
    },
    is_featured: true,
    order: 0
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (isLoggedIn) {
      setIsAuthenticated(true);
      setCheckingAuth(false);
      fetchMetadata();
      if (!isNewProject) {
        fetchProject();
      }
      return;
    }
    
    const token = localStorage.getItem('adminToken');
    const expires = localStorage.getItem('adminTokenExpires');

    if (!token || !expires) {
      navigate('/admin/login');
      return;
    }

    if (new Date(expires) < new Date()) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminTokenExpires');
      navigate('/admin/login');
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/admin/validate`, { token });
      setIsAuthenticated(true);
      fetchMetadata();
      if (!isNewProject) {
        fetchProject();
      }
    } catch (error) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminTokenExpires');
      navigate('/admin/login');
    } finally {
      setCheckingAuth(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const [categoriesRes, scopeRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects/categories`),
        axios.get(`${BACKEND_URL}/api/projects/scope-items`)
      ]);
      setAllCategories(categoriesRes.data);
      setAllScopeItems(scopeRes.data);
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects?include_unpublished=true`);
      const project = response.data.find(p => p.id === projectId);
      
      if (!project) {
        alert('Project not found');
        navigate('/admin/projects');
        return;
      }

      // Map separate image fields to categorized_images format
      const categorizedImages = project.categorized_images || {
        before: project.before_images || [],
        progress: project.during_images || [],
        completion: project.completion_images || []
      };

      setFormData({
        name: project.name,
        slug: project.slug,
        description: project.description,
        featured_image: project.featured_image,
        categories: project.categories.join(', '),
        client_name: project.client_name || '',
        location: project.location || '',
        status: project.status,
        scope_of_work: project.scope_of_work.join(', '),
        highlighted_scope: project.highlighted_scope || [],
        testimonial_quote: project.testimonial_quote || '',
        testimonial_author: project.testimonial_author || '',
        video_url: project.video_url || '',
        video_title: project.video_title || '',
        testimonial_video_url: project.testimonial_video_url || '',
        testimonial_video_title: project.testimonial_video_title || '',
        images: project.images || [],
        categorized_images: categorizedImages,
        is_featured: project.is_featured,
        order: project.order
      });
    } catch (error) {
      console.error('Error fetching project:', error);
      alert('Error loading project');
      navigate('/admin/projects');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'name' && isNewProject ? { slug: generateSlug(value) } : {})
    }));
  };

  const handleAddHighlightedScope = () => {
    setFormData(prev => ({
      ...prev,
      highlighted_scope: [...prev.highlighted_scope, { title: '', description: '' }]
    }));
  };

  const handleHighlightedScopeChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.highlighted_scope];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, highlighted_scope: updated };
    });
  };

  const handleRemoveHighlightedScope = (index) => {
    setFormData(prev => ({
      ...prev,
      highlighted_scope: prev.highlighted_scope.filter((_, i) => i !== index)
    }));
  };

  const handleFeaturedImageUpload = (url) => {
    setFormData(prev => ({ ...prev, featured_image: url }));
  };

  const handleCategorizedImagesChange = (category, images) => {
    setFormData(prev => ({
      ...prev,
      categorized_images: {
        ...prev.categorized_images,
        [category]: images
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Map categorized_images back to separate fields for database
    const projectData = {
      ...formData,
      categories: formData.categories.split(',').map(c => c.trim()).filter(c => c),
      scope_of_work: formData.scope_of_work.split(',').map(s => s.trim()).filter(s => s),
      order: parseInt(formData.order) || 0,
      // Map to separate fields for backend
      before_images: formData.categorized_images?.before || [],
      during_images: formData.categorized_images?.progress || [],
      completion_images: formData.categorized_images?.completion || []
    };

    try {
      if (isNewProject) {
        await axios.post(`${BACKEND_URL}/api/projects`, projectData);
      } else {
        await axios.put(`${BACKEND_URL}/api/projects/${projectId}`, projectData);
      }
      navigate('/admin/projects');
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Error saving project: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (checkingAuth || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">
          {checkingAuth ? 'Checking authentication...' : 'Loading project...'}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <div className="pt-40 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin/projects')}
              className="p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-3xl font-bold text-slate-900">
              {isNewProject ? 'Create New Project' : 'Edit Project'}
            </h1>
          </div>

          {/* Form */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Project Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="slug">URL Slug *</Label>
                  <Input
                    id="slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="mt-1"
                />
              </div>

              {/* Featured Image Upload */}
              <div className="space-y-3">
                <ImageUploader
                  label="Featured Image *"
                  onUpload={handleFeaturedImageUpload}
                />
                {formData.featured_image && (
                  <div className="relative inline-block">
                    <img
                      src={formData.featured_image.startsWith('/api') ? `${BACKEND_URL}${formData.featured_image}` : formData.featured_image}
                      alt="Featured"
                      className="w-32 h-32 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, featured_image: '' }))}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <div>
                  <Label htmlFor="featured_image_url" className="text-sm text-slate-500">Or enter URL directly</Label>
                  <Input
                    id="featured_image_url"
                    name="featured_image"
                    value={formData.featured_image}
                    onChange={handleInputChange}
                    className="mt-1"
                    placeholder="https://..."
                  />
                </div>
              </div>

              {/* Categories with Autocomplete */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <AutoCompleteInput
                  label="Categories"
                  value={formData.categories}
                  onChange={(val) => setFormData(prev => ({ ...prev, categories: val }))}
                  suggestions={allCategories}
                  placeholder="Type to add or select categories..."
                />
                <div>
                  <Label htmlFor="order">Display Order</Label>
                  <Input
                    id="order"
                    name="order"
                    type="number"
                    value={formData.order}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="client_name">Client Name</Label>
                  <Input
                    id="client_name"
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="mt-1"
                    placeholder="Charlotte, NC"
                  />
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  >
                    <option value="Upcoming">Upcoming</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
              </div>

              {/* Scope of Work with Autocomplete */}
              <AutoCompleteInput
                label="Scope of Work"
                value={formData.scope_of_work}
                onChange={(val) => setFormData(prev => ({ ...prev, scope_of_work: val }))}
                suggestions={allScopeItems}
                placeholder="Type to add or select scope items..."
              />

              {/* Highlighted Scope */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Highlighted Scope Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddHighlightedScope}>
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                {formData.highlighted_scope.map((item, index) => (
                  <div key={index} className="flex gap-4 mb-3 p-4 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <Input
                        placeholder="Title"
                        value={item.title}
                        onChange={(e) => handleHighlightedScopeChange(index, 'title', e.target.value)}
                        className="mb-2"
                      />
                      <Textarea
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => handleHighlightedScopeChange(index, 'description', e.target.value)}
                        rows={2}
                      />
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveHighlightedScope(index)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="testimonial_quote">Testimonial Quote</Label>
                  <Textarea
                    id="testimonial_quote"
                    name="testimonial_quote"
                    value={formData.testimonial_quote}
                    onChange={handleInputChange}
                    className="mt-1"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="testimonial_author">Testimonial Author</Label>
                  <Input
                    id="testimonial_author"
                    name="testimonial_author"
                    value={formData.testimonial_author}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Video URLs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="video_url">Project Video URL (YouTube)</Label>
                  <Input
                    id="video_url"
                    name="video_url"
                    value={formData.video_url}
                    onChange={handleInputChange}
                    className="mt-1"
                    placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
                  />
                  <p className="text-xs text-slate-400 mt-1">Supports youtube.com/watch, youtu.be, or embed URLs</p>
                </div>
                <div>
                  <Label htmlFor="video_title">Project Video Title</Label>
                  <Input
                    id="video_title"
                    name="video_title"
                    value={formData.video_title}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Testimonial Video URLs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="testimonial_video_url">Testimonial Video URL (YouTube)</Label>
                  <Input
                    id="testimonial_video_url"
                    name="testimonial_video_url"
                    value={formData.testimonial_video_url}
                    onChange={handleInputChange}
                    className="mt-1"
                    placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
                  />
                  <p className="text-xs text-slate-400 mt-1">Supports youtube.com/watch, youtu.be, or embed URLs</p>
                </div>
                <div>
                  <Label htmlFor="testimonial_video_title">Testimonial Video Title</Label>
                  <Input
                    id="testimonial_video_title"
                    name="testimonial_video_title"
                    value={formData.testimonial_video_title}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Categorized Project Images - Improved Layout */}
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b pb-3">
                  <Label className="text-lg font-semibold">Project Gallery</Label>
                  <p className="text-sm text-slate-500">Organize photos by project phase</p>
                </div>
                
                {/* Completion First */}
                <div className="bg-green-50 border-2 border-green-300 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-green-800">Completion Photos</h4>
                      <p className="text-sm text-green-600">{formData.categorized_images.completion.length} photos uploaded</p>
                    </div>
                  </div>
                  <CategorizedImageGallery
                    category="completion"
                    categoryLabel=""
                    categoryColor=""
                    images={formData.categorized_images.completion}
                    onImagesChange={(images) => handleCategorizedImagesChange('completion', images)}
                  />
                </div>
                
                {/* Progress Second */}
                <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-blue-800">Progress Photos</h4>
                      <p className="text-sm text-blue-600">{formData.categorized_images.progress.length} photos uploaded</p>
                    </div>
                  </div>
                  <CategorizedImageGallery
                    category="progress"
                    categoryLabel=""
                    categoryColor=""
                    images={formData.categorized_images.progress}
                    onImagesChange={(images) => handleCategorizedImagesChange('progress', images)}
                  />
                </div>
                
                {/* Before Third */}
                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-amber-800">Before Photos</h4>
                      <p className="text-sm text-amber-600">{formData.categorized_images.before.length} photos uploaded</p>
                    </div>
                  </div>
                  <CategorizedImageGallery
                    category="before"
                    categoryLabel=""
                    categoryColor=""
                    images={formData.categorized_images.before}
                    onImagesChange={(images) => handleCategorizedImagesChange('before', images)}
                  />
                </div>
              </div>

              <div className="flex gap-4 pt-6 border-t">
                <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700" disabled={saving}>
                  <Save className="w-5 h-5 mr-2" />
                  {saving ? 'Saving...' : (isNewProject ? 'Create Project' : 'Save Changes')}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate('/admin/projects')}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminEditProjectPage;
